import { useState, useMemo } from "react";

import { PageHeader } from "@/components/shared/PageHeader";
import { EntityGrid, EntityGridSkeleton } from "@/components/shared/EntityGrid";
import { DoctorCard } from "@/components/doctors/DoctorCard";
import { CategoryFilter, type CategoryItem } from "@/components/shared/CategoryFilter";
import { useDoctors } from "@/hooks/useDoctors";
import { Stethoscope, Heart, Bone, Activity, Baby, Repeat, Eye, Sparkles, Leaf, Dumbbell, Brain } from "lucide-react";
import { SEOHead } from "@/components/seo";
import { SEO_METADATA } from "@/lib/seo-metadata";
import { COMPANY } from "@/config/company";

const specialtyCategories: CategoryItem[] = [
  { slug: "dental", name: "Dental", icon: <Stethoscope className="w-5 h-5" /> },
  { slug: "cardiac", name: "Cardiac", icon: <Heart className="w-5 h-5" /> },
  { slug: "orthopedics", name: "Orthopedics", icon: <Bone className="w-5 h-5" /> },
  { slug: "oncology", name: "Oncology", icon: <Activity className="w-5 h-5" /> },
  { slug: "fertility", name: "Fertility", icon: <Baby className="w-5 h-5" /> },
  { slug: "transplants", name: "Transplants", icon: <Repeat className="w-5 h-5" /> },
  { slug: "ophthalmology", name: "Eye Care", icon: <Eye className="w-5 h-5" /> },
  { slug: "cosmetic", name: "Cosmetic", icon: <Sparkles className="w-5 h-5" /> },
  { slug: "wellness", name: "Wellness", icon: <Leaf className="w-5 h-5" /> },
  { slug: "rehabilitation", name: "Rehab", icon: <Dumbbell className="w-5 h-5" /> },
  { slug: "mental_health", name: "Mental Health", icon: <Brain className="w-5 h-5" /> },
];

// Map specialty names to category slugs for filtering
const specialtyToCategory: Record<string, string> = {
  "Dental Surgery": "dental",
  "Cardiology": "cardiac",
  "Cardiac Surgery": "cardiac",
  "Orthopedics": "orthopedics",
  "Orthopedic Surgery": "orthopedics",
  "Oncology": "oncology",
  "Fertility": "fertility",
  "IVF Specialist": "fertility",
  "Transplant Surgery": "transplants",
  "Ophthalmology": "ophthalmology",
  "Cosmetic Surgery": "cosmetic",
  "Plastic Surgery": "cosmetic",
  "Wellness": "wellness",
  "Rehabilitation": "rehabilitation",
  "Mental Health": "mental_health",
  "Psychiatry": "mental_health",
};

export default function Doctors() {
  const { data: doctors = [], isLoading } = useDoctors();
  const [activeCategory, setActiveCategory] = useState<string | undefined>(undefined);

  const filteredDoctors = useMemo(() => {
    if (!activeCategory) return doctors;
    
    return doctors.filter((doctor) => {
      const categorySlug = specialtyToCategory[doctor.specialty] || doctor.specialty.toLowerCase();
      return categorySlug === activeCategory;
    });
  }, [doctors, activeCategory]);

  // Custom handler since we're not using URL params for doctors
  const handleCategoryClick = (slug: string | undefined) => {
    setActiveCategory(slug);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={SEO_METADATA.doctors.title}
        description={SEO_METADATA.doctors.description}
        canonicalPath="/doctors/"
        keywords={SEO_METADATA.doctors.keywords}
      />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <PageHeader
            title="Our Expert Doctors"
            description="Internationally trained specialists with decades of experience and thousands of successful procedures"
          />

          {/* Specialty Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <button
              onClick={() => handleCategoryClick(undefined)}
              className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium transition-all duration-200 h-10 px-4 py-2 ${
                !activeCategory
                  ? "bg-primary text-primary-foreground"
                  : "border border-input bg-background hover:bg-accent/10 hover:text-primary hover:border-primary"
              }`}
            >
              All
            </button>
            {specialtyCategories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryClick(cat.slug)}
                className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium transition-all duration-200 h-10 px-4 py-2 ${
                  activeCategory === cat.slug
                    ? "bg-primary text-primary-foreground"
                    : "border border-input bg-background hover:bg-accent/10 hover:text-primary hover:border-primary"
                }`}
              >
                {cat.icon}
                {cat.name}
              </button>
            ))}
          </div>


          {isLoading ? (
            <EntityGridSkeleton count={6} columns={3} aspectRatio="square" />
          ) : filteredDoctors.length === 0 ? (
            <div className="text-center py-16">
              <div className="max-w-md mx-auto">
                <p className="text-muted-foreground text-lg mb-2">Our specialists in this area are currently attending to patients.</p>
                <p className="text-muted-foreground">Please check back shortly or explore other specialties – we have excellent doctors ready to help!</p>
              </div>
            </div>
          ) : (
            <>
              <h2 className="sr-only">Available Specialists</h2>
              <EntityGrid columns={3}>
                {filteredDoctors.map((doctor) => (
                  <DoctorCard key={doctor.id} doctor={doctor} />
                ))}
              </EntityGrid>
            </>
          )}

          {/* Sources & Citations */}
          <div className="mt-12 max-w-4xl mx-auto">
            <h2 className="font-display text-xl font-semibold text-secondary mb-3">
              Sources & Citations
            </h2>
            <p className="text-sm text-muted-foreground mb-3">
              Accreditation references are provided below. Individual credentials and experience should be verified
              during consultation.
            </p>
            <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-2">
              <li>
                <a
                  href="https://www.jointcommissioninternational.org/what-we-offer/accreditation/"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-primary hover:underline"
                >
                  JCI accreditation overview (Joint Commission International)
                </a>
              </li>
              <li>
                <a
                  href="https://nabh.co/faq-nabh/"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-primary hover:underline"
                >
                  NABH overview and recognition (NABH)
                </a>
              </li>
            </ul>
          </div>
        </div>
      </main>

    </div>
  );
}
